import React, { createContext, useCallback, useContext, useRef } from 'react';
import { ChatMessage } from '../data/mockChatData';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DisplayMsg {
  id: string;
  sender: 'tutor' | 'user';
  text: string;
  timestamp: string;
  isCorrect?: boolean;
  characterImage?: any;
  characterName?: string;
}

export interface AnsweredQuestion {
  id: string;
  question: ChatMessage;
  selectedAnswer: string | string[] | null;
  timestamp: string;
  /** Toutes les options essayees sur ce noeud (dont la selection finale). */
  attemptedAnswers?: string[];
}

/** Panneau de choix persistant dans le fil (missions arborescentes). */
export interface ChoiceTurnData {
  id: string;
  nodeId: string;
  options: { id: string; label: string }[];
  attemptedLabels: string[];
  pendingLabel: string | null;
  selectedLabel: string | null;
  resolved: boolean;
}

export type ChatItem =
  | { kind: 'msg'; data: DisplayMsg }
  | { kind: 'answered'; data: AnsweredQuestion }
  | { kind: 'choice-turn'; data: ChoiceTurnData };

export interface ChatState {
  chatItems: ChatItem[];
  /** Position dans le script lineaire (missions quiz) */
  pos: number;
  /** Nombre de choix / reponses enregistres */
  answeredCount: number;
  isComplete: boolean;
  /** Discussion arborescente : noeud courant */
  currentNodeId?: string;
  /** Options deja essayees par noeud (session en cours). */
  attemptedChoicesByNode?: Record<string, string[]>;
}

export type ChatStateKey = string;
export const buildChatStateKey = (missionId: number, itemId?: string | number | null): ChatStateKey =>
  `${missionId}::${itemId ?? '__default__'}`;

interface ChatStateContextType {
  getState: (key: ChatStateKey) => ChatState | undefined;
  setState: (key: ChatStateKey, state: ChatState) => void;
  clearState: (key: ChatStateKey) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ChatStateContext = createContext<ChatStateContextType | undefined>(undefined);

export const useChatState = () => {
  const ctx = useContext(ChatStateContext);
  if (ctx === undefined) {
    throw new Error('useChatState must be used within a ChatStateProvider');
  }
  return ctx;
};

export const ChatStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeRef = useRef<Map<ChatStateKey, ChatState>>(new Map());

  const getState = useCallback((key: ChatStateKey) => {
    return storeRef.current.get(key);
  }, []);

  const setState = useCallback((key: ChatStateKey, state: ChatState) => {
    storeRef.current.set(key, state);
  }, []);

  const clearState = useCallback((key: ChatStateKey) => {
    storeRef.current.delete(key);
  }, []);

  return (
    <ChatStateContext.Provider value={{ getState, setState, clearState }}>
      {children}
    </ChatStateContext.Provider>
  );
};
